import { getShipToCodes } from '../../services/orders'
import { useGetBuildingFromInvoice } from './building-from-invoice.hook'
import { useEffect, useMemo } from 'react'
import { useGetCustomerPordFile } from './customer.hook'
import { useAuthHook } from './auth.hooks'
import { queryClient } from '../../context/constants'
import { useSentryQuery } from '../useSentryQuery'

export const useShipToCodes = ({
    callBackFn = () => {},
}: {
    callBackFn?: (data: {
        allBuildings: {
            label: string
            value: string
        }[]

        allUnits: {
            label: string
            value: string
        }[]

        allLocations: {
            label: string
            value: string
        }[]
    }) => void
}) => {
    const { data: userData } = useAuthHook()

    const {
        data: buildingFromInvoice,
        isLoading: isBuildingFromInvoiceLoading,
    } = useGetBuildingFromInvoice()
    const {
        customerData,
        isCustomerLoading,
        custAddrError,
        customerError,
        pordFileError,
    } = useGetCustomerPordFile()

    const { data: defaultSelectedLocation } = useGetDefautLocation()

    const shipToCodesData = useSentryQuery({
        queryKey: ['getShipToCodes', userData],
        staleTime: 180000,
        enabled: Boolean(buildingFromInvoice),
        queryFn: async ({ signal }) => {
            const shipToCodes = await getShipToCodes({ signal })
            const allLocations = [
                { id: 'all', label: 'Select All', value: 'all' },
                ...shipToCodes.map((item) => ({
                    id: `${item.CADD_CUST},${item.CADD_ADDRCD}`,
                    label: `${item.CADD_CUST} - ${item.CADD_NAME} (${item.CADD_ADDR1})`,
                    value: `${item.CADD_CUST},${item.CADD_ADDRCD}`,
                })),
            ]
            return {
                allBuildings: buildingFromInvoice?.buildings,
                allUnits: buildingFromInvoice?.units,
                allLocations: allLocations,
            }
        },
    })

    /**
     * Saves an item in local storage under the key 'defaultSelectedLocation'.
     * If the item already exists, it moves it to the beginning of the list.
     *
     * @param {string} item - The item to be saved in local storage.
     */
    const saveInLocalStorage = (item: string) => {
        localStorage.setItem('defaultSelectedLocation', item)
        queryClient.invalidateQueries({
            queryKey: ['getDefaultLocation', userData?.userDetails?.id],
        })
    }

    useEffect(() => {
        if (!shipToCodesData?.data) {
            return
        }

        if (!defaultSelectedLocation) {
            saveInLocalStorage(shipToCodesData?.data?.allLocations[1].value)
        }
        callBackFn({
            allLocations: shipToCodesData?.data?.allLocations,
            allBuildings: shipToCodesData?.data?.allBuildings,
            allUnits: shipToCodesData?.data?.allUnits,
        })
    }, [shipToCodesData.data, buildingFromInvoice])

    const shipToItems = useMemo(() => {
        if (!customerData?.length) {
            return []
        }
        return customerData?.map((customer) => ({
            id: customer.C_CID,
            value: customer.C_CID ?? '',
            label: `${customer.C_CID} - ${customer.C_NAME} (${customer.C_ADDR1})`,
        }))
    }, [customerData])

    if (!isCustomerLoading && shipToItems.length === 0) {
        console.log('error===', custAddrError, customerError, pordFileError)
        throw new Error('No locations found')
    }

    return {
        ...shipToCodesData,
        isBuildingFromInvoiceLoading,
        shipToItems,
        defaultSelectedLocation,
        saveInLocalStorage,
    }
}

export const useGetDefautLocation = () => {
    const { data: userData } = useAuthHook()
    return useSentryQuery({
        queryKey: ['getDefaultLocation', userData?.userDetails?.id],
        staleTime: Infinity,
        queryFn: async () => {
            return localStorage.getItem('defaultSelectedLocation')
        },
    })
}
